// Set REACT_APP_API_URL (see .env.production) to the address of the Django app when building for the live site.
export const API_BASE_URL = (process.env.REACT_APP_API_URL || "http://127.0.0.1:8000").replace(/\/+$/, "");

export async function fetchMenu() {
  const res = await fetch(`${API_BASE_URL}/core/menu/`);
  if (!res.ok) throw new Error("Failed to load menu");
  return res.json();
}

export async function fetchHero(slug = "home") {
  const res = await fetch(`${API_BASE_URL}/core/hero/${slug}/`);
  if (!res.ok) throw new Error("Failed to load hero content");
  return res.json();
}

export async function fetchServices() {
  const res = await fetch(`${API_BASE_URL}/core/services/`);
  if (!res.ok) throw new Error("Failed to load services");
  return res.json();
}

export async function fetchServiceById(id) {
  const res = await fetch(`${API_BASE_URL}/core/services/${id}/`);
  if (!res.ok) throw new Error("Failed to load service");
  return res.json();
}

export async function fetchServiceCards() {
  const res = await fetch(`${API_BASE_URL}/core/service-cards/`);
  if (!res.ok) throw new Error("Failed to load service cards");
  return res.json();
}

export async function fetchServiceCard(id) {
  const res = await fetch(`${API_BASE_URL}/core/service-cards/${id}/`);
  if (!res.ok) throw new Error("Failed to load service");
  return res.json();
}

export async function fetchSubServiceCards() {
  const res = await fetch(`${API_BASE_URL}/core/sub-services/`);
  if (!res.ok) throw new Error("Failed to load sub-services");
  return res.json();
}

export async function fetchSystems() {
  const res = await fetch(`${API_BASE_URL}/core/systems/`);
  if (!res.ok) throw new Error("Failed to load systems");
  return res.json();
}

export async function fetchSystem(id) {
  const res = await fetch(`${API_BASE_URL}/core/systems/${id}/`);
  if (!res.ok) throw new Error("Failed to load system");
  return res.json();
}

// The whole detail page of a system (hero, stats, features, modules, plans and prices, quote texts, contact). Rejects when it has none.
export async function fetchSystemPage(id) {
  const res = await fetch(`${API_BASE_URL}/core/systems/${id}/page/`);
  if (!res.ok) throw new Error("This system has no detail page");
  return res.json();
}

// The two-letter country of the visitor from their IP address ("" when it cannot be told), for showing prices in their currency.
export async function fetchVisitorCountry() {
  try {
    const res = await fetch(`${API_BASE_URL}/core/geo/country/`);
    if (!res.ok) return "";
    return String((await res.json()).country || "");
  } catch {
    return "";
  }
}

// ---- Text-to-Speech (free, local engine - no API key) ----

export async function fetchTTSVoices() {
  const res = await fetch(`${API_BASE_URL}/core/tts/voices/`);
  if (!res.ok) throw new Error("Failed to load voices");
  return res.json();
}

export async function generateTTS(payload) {
  const res = await fetch(`${API_BASE_URL}/core/tts/generate/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.text || data.detail || "Failed to generate audio");
  return data;
}

export async function fetchTTSHistory(clientId) {
  const res = await fetch(`${API_BASE_URL}/core/tts/history/?client_id=${encodeURIComponent(clientId)}`);
  if (!res.ok) throw new Error("Failed to load history");
  return res.json();
}

export async function deleteTTSHistory(id, clientId) {
  const res = await fetch(`${API_BASE_URL}/core/tts/history/${id}/?client_id=${encodeURIComponent(clientId)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete");
}

export async function submitQuoteRequest(payload) {
  const res = await fetch(`${API_BASE_URL}/core/quote-requests/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to submit request");
  return res.json();
}

export async function fetchSiteIdentity() {
  const res = await fetch(`${API_BASE_URL}/core/site-identity/`);
  if (!res.ok) throw new Error("Failed to load site identity");
  return res.json();
}

export async function fetchNavbarSettings() {
  const res = await fetch(`${API_BASE_URL}/core/navbar/`);
  if (!res.ok) throw new Error("Failed to load navbar settings");
  return res.json();
}

export async function fetchFooterContent() {
  const res = await fetch(`${API_BASE_URL}/core/footer/`);
  if (!res.ok) throw new Error("Failed to load footer content");
  return res.json();
}

// Turns a flat [{id, parent, order, ...}] list into a nested tree (children sorted by order).
export function buildMenuTree(items) {
  const nodes = {};
  items.forEach((item) => { nodes[item.id] = { ...item, children: [] }; });
  const roots = [];
  items.forEach((item) => {
    if (item.parent && nodes[item.parent]) {
      nodes[item.parent].children.push(nodes[item.id]);
    } else if (!item.parent) {
      roots.push(nodes[item.id]);
    }
  });
  const sortTree = (list) => {
    list.sort((a, b) => a.order - b.order);
    list.forEach((node) => sortTree(node.children));
  };
  sortTree(roots);
  return roots;
}

// ---- Auth ----

export function getAccessToken() {
  return localStorage.getItem("access_token");
}

function getRefreshToken() {
  return localStorage.getItem("refresh_token");
}

export function isAuthenticated() {
  return !!getAccessToken();
}

export function logout() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
}

export async function login(email, password) {
  const res = await fetch(`${API_BASE_URL}/api/token/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username: email, password }),
  });
  if (!res.ok) throw new Error("Invalid email or password");
  const data = await res.json();
  localStorage.setItem("access_token", data.access);
  localStorage.setItem("refresh_token", data.refresh);
}

async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  const res = await fetch(`${API_BASE_URL}/api/token/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });
  if (!res.ok) return null;
  const data = await res.json();
  localStorage.setItem("access_token", data.access);
  return data.access;
}

export async function authFetch(path, options = {}) {
  const doFetch = (token) =>
    fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });

  let res = await doFetch(getAccessToken());
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      logout();
      throw new Error("Session expired");
    }
    res = await doFetch(newToken);
  }
  return res;
}

// For multipart file uploads (e.g. logo/favicon) — no Content-Type header, so the browser
// sets the correct multipart boundary itself.
export async function authFetchForm(path, formData, method = "PATCH") {
  const doFetch = (token) =>
    fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

  let res = await doFetch(getAccessToken());
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      logout();
      throw new Error("Session expired");
    }
    res = await doFetch(newToken);
  }
  return res;
}

// ---- Generic helpers for the marketplace / foundation / portfolio features ----

// One request helper: JSON or FormData body, optional JWT (refreshed once on 401). Never throws -
// returns { ok, status, data } so callers can show the server's validation messages.
export async function apiRequest(path, { method = "GET", body, auth = false } = {}) {
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  try {
    let res;
    if (auth) {
      res = isForm
        ? await authFetchForm(path, body, method)
        : await authFetch(path, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
    } else {
      res = await fetch(`${API_BASE_URL}${path}`, {
        method,
        ...(body === undefined ? {} : isForm ? { body } : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
      });
    }
    let data = null;
    try { data = await res.json(); } catch { /* 204 / empty body */ }
    return { ok: res.ok, status: res.status, data };
  } catch (err) {
    const expired = err && err.message === "Session expired";
    return { ok: false, status: expired ? 401 : 0, data: { detail: expired ? "Your session expired. Please sign in again." : "Could not reach the server." } };
  }
}

// Turns a DRF error payload ({field: ["msg"]} or {detail: "msg"}) into one readable sentence.
export function errorMessage(data, fallback = "Something went wrong. Please try again.") {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.detail) return String(data.detail);
  const parts = Object.entries(data).map(([field, msgs]) => {
    const text = Array.isArray(msgs) ? msgs.join(" ") : typeof msgs === "object" ? errorMessage(msgs, "") : String(msgs);
    const label = field === "non_field_errors" ? "" : `${field.replace(/_/g, " ")}: `;
    return `${label}${text}`;
  });
  return parts.filter(Boolean).join(" ") || fallback;
}

export async function fetchCoWorkSlides() {
  const res = await fetch(`${API_BASE_URL}/core/cowork/slides/`);
  if (!res.ok) throw new Error("Failed to load slides");
  return res.json();
}

export async function fetchCoWorkSettings() {
  const res = await fetch(`${API_BASE_URL}/core/cowork/settings/`);
  if (!res.ok) throw new Error("Failed to load settings");
  return res.json();
}

export async function fetchFoundationSettings() {
  const res = await fetch(`${API_BASE_URL}/core/foundation/settings/`);
  if (!res.ok) throw new Error("Failed to load settings");
  return res.json();
}

export async function fetchProjects(category = "") {
  const res = await fetch(`${API_BASE_URL}/core/projects/${category ? `?category=${encodeURIComponent(category)}` : ""}`);
  if (!res.ok) throw new Error("Failed to load projects");
  return res.json();
}

// ---- Blogs ----
export async function fetchBlogPosts(params = {}) {
  const query = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
  const { ok, data } = await apiRequest(`/core/blog-posts/${query ? `?${query}` : ""}`);
  if (!ok) throw new Error("Failed to load blog posts");
  return data;
}

export async function fetchBlogCategories() {
  const { ok, data } = await apiRequest("/core/blog-categories/");
  if (!ok) throw new Error("Failed to load blog categories");
  return data;
}

// "123.50" -> "123.50", "1200000.00" -> "1,200,000" (thousands separators, drop empty decimals)
export function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// "now", "5m", "3h", "2d", "4w" - compact relative time for chat lists.
export function timeAgoShort(iso) {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return `${Math.floor(seconds / 604800)}w`;
}
